import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { getStickyHeaderBottom } from '../../utils/stickyHeader';

interface DateInputProps {
  value: string; // Format YYYY-MM-DD (peut être vide)
  onChange: (date: string) => void;
  onConfirm?: () => void; // Appelé après Enter/Tab pour signaler la fin de saisie
  onBlur?: () => void; // Appelé quand le composant perd le focus
  onInputStart?: () => void; // Appelé dès que l'utilisateur commence à saisir
  onValidationError?: (error: string | null) => void; // Appelé quand une erreur de validation se produit ou est résolue
  minDate?: string; // Format YYYY-MM-DD
  maxDate?: string; // Format YYYY-MM-DD
  minDateError?: string; // Message d'erreur si date < minDate
  maxDateError?: string; // Message d'erreur si date > maxDate
  defaultMonth?: number; // Mois par défaut à afficher (1-12) si pas de valeur
  defaultYear?: number; // Année par défaut à afficher si pas de valeur
  /**
   * Plage à confirmer (YYYY-MM-DD, bornes incluses) : une date valide mais hors
   * de [min, max] n'est pas appliquée, elle est transmise à `onRequest`. À
   * l'appelant de la confirmer en la repassant via `value`, ou de l'écarter.
   */
  confirmOutside?: { min: string; max: string; onRequest: (date: string) => void };
  disabled?: boolean;
  compact?: boolean; // Mode compact : affiche jj/mm sans année, masque l'icône calendrier
  className?: string;
}

// Interface pour exposer les méthodes du composant via ref
export interface DateInputRef {
  /** `preventScroll` : l'appelant place lui-même la ligne dans la fenêtre. */
  focus: (options?: FocusOptions) => void;
}

const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Convertir YYYY-MM-DD en jj/mm/aaaa
function formatToDisplay(dateStr: string, compact = false): string {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  if (compact) return `${day}/${month}`;
  return `${day}/${month}/${year}`;
}

// Convertir jj/mm/aaaa en YYYY-MM-DD
function formatToISO(displayStr: string): string {
  if (!displayStr || displayStr.length !== 10) return '';
  const parts = displayStr.split('/');
  if (parts.length !== 3) return '';
  const [day, month, year] = parts;
  return `${year}-${month}-${day}`;
}

// Valider une date
function isValidDate(day: number, month: number, year: number): boolean {
  if (year < 2000 || year > 2100) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Vérifier le nombre de jours dans le mois
  const daysInMonth = new Date(year, month, 0).getDate();
  return day <= daysInMonth;
}

// Parser une date affichée
function parseDisplayDate(displayStr: string): { day: number; month: number; year: number } | null {
  const parts = displayStr.split('/');
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  if (!isValidDate(day, month, year)) return null;

  return { day, month, year };
}

const DateInput = forwardRef<DateInputRef, DateInputProps>(({
  value,
  onChange,
  onConfirm,
  onBlur,
  onInputStart,
  onValidationError,
  minDate,
  maxDate,
  minDateError,
  maxDateError,
  defaultMonth,
  defaultYear,
  confirmOutside,
  disabled = false,
  compact = false,
  className = '',
}, ref) => {
  const { t } = useTranslation('common');
  const MONTHS = MONTH_KEYS.map(key => t(`months.${key}`));
  const DAYS = DAY_KEYS.map(key => t(`days.${key}`));

  const MONTHS_SHORT = MONTH_KEYS.map(key => t(`monthsShort.${key}`));

  const [displayValue, setDisplayValue] = useState(formatToDisplay(value, compact));
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarView, setCalendarView] = useState<'days' | 'months'>('days');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fermer le calendrier et réinitialiser la vue
  const closeCalendar = useCallback(() => {
    setIsCalendarOpen(false);
    setCalendarView('days');
  }, []);

  // Effacer le message d'erreur
  const clearError = useCallback(() => {
    setErrorMessage(null);
    onValidationError?.(null);
  }, [onValidationError]);

  // Afficher un message d'erreur (reste jusqu'à saisie valide)
  const showError = useCallback((message: string) => {
    setErrorMessage(message);
    onValidationError?.(message);
  }, [onValidationError]);

  // Déterminer le mois/année par défaut à afficher
  const getDefaultCalendarMonth = useCallback(() => {
    if (value) {
      const [year, month] = value.split('-').map(Number);
      return { month, year };
    }
    // Utiliser le mois/année par défaut si fournis
    if (defaultMonth && defaultYear) {
      return { month: defaultMonth, year: defaultYear };
    }
    // Sinon, utiliser le mois en cours
    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
  }, [value, defaultMonth, defaultYear]);

  const [calendarMonth, setCalendarMonth] = useState(getDefaultCalendarMonth);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0, width: 280 });

  const inputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const isClickingCalendarRef = useRef(false);
  const hasStartedInputRef = useRef(false);

  // Exposer la méthode focus via la ref
  useImperativeHandle(ref, () => ({
    focus: (options?: FocusOptions) => {
      inputRef.current?.focus(options);
    },
  }));

  // Passer au champ suivant
  const focusNextField = useCallback(() => {
    // Utiliser requestAnimationFrame + setTimeout pour s'assurer que React a terminé le re-render
    // Cela permet aux champs conditionnellement disabled (ex: sous-catégorie) d'être activés
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Sélecteur pour les éléments focusables (exclut les boutons avec tabindex=-1)
        const focusableSelector = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([tabindex="-1"])';

        // Obtenir tous les éléments focusables du document
        const allFocusable = Array.from(document.querySelectorAll(focusableSelector)) as HTMLElement[];
        const currentIndex = allFocusable.indexOf(inputRef.current!);

        if (currentIndex >= 0 && currentIndex < allFocusable.length - 1) {
          const nextElement = allFocusable[currentIndex + 1];
          nextElement.focus();
        }
      }, 0);
    });
  }, []);

  // Mettre à jour displayValue quand value change de l'extérieur
  useEffect(() => {
    setDisplayValue(formatToDisplay(value, compact));
    // Réinitialiser le flag si la valeur est vidée
    if (!value) {
      hasStartedInputRef.current = false;
    }
  }, [value, compact]);

  // Calculer la position du calendrier
  const updateCalendarPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const calendarWidth = 280;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 8;
      const gap = 4;
      // Hauteur réelle du calendrier si déjà rendu, sinon estimation (vue jours)
      const calendarHeight = calendarRef.current?.offsetHeight || 340;

      // Clamper à gauche pour ne pas dépasser le bord droit
      const left = Math.min(rect.left, viewportWidth - calendarWidth - margin);

      // Placer en dessous par défaut, basculer au-dessus si pas assez de place
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      let top = rect.bottom + gap;
      if (spaceBelow < calendarHeight + margin && spaceAbove > spaceBelow) {
        top = Math.max(margin, rect.top - calendarHeight - gap);
      }

      setDropdownPosition({
        left: Math.max(margin, left),
        top,
        width: calendarWidth,
      });
    }
  }, []);

  /**
   * Dégage la place SOUS le champ pour que le calendrier s'ouvre vers le bas —
   * son sens naturel — en défilant la page quand elle le permet. Il ne bascule
   * au-dessus (updateCalendarPosition) que si le défilement n'y suffit pas :
   * bas de page atteint, ou ligne éditée qui passerait sous la barre du haut.
   *
   * Défilement instantané, à dessein : le calendrier est positionné dans la
   * foulée, une animation le ferait basculer haut/bas pendant le trajet.
   */
  const scrollForCalendar = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    const margin = 8;
    const gap = 4;
    const calendarHeight = calendarRef.current?.offsetHeight || 340;
    // La ligne éditée quand le champ en balise une, sinon le champ seul.
    const anchor = (input.closest('[data-scroll-anchor]') as HTMLElement | null) ?? input;
    const inputRect = input.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const topLimit = getStickyHeaderBottom() + margin;

    // Ligne déjà passée sous la barre du haut : on la redescend, rien d'autre.
    if (anchorRect.top < topLimit) {
      window.scrollBy({ top: anchorRect.top - topLimit });
      return;
    }

    // Défilement voulu : la hauteur du calendrier sous le champ, et au minimum
    // la ligne entière dans la fenêtre. `slack` couvre les quelques pixels que
    // la ligne d'édition gagne encore en se dépliant : sans lui, il manque
    // parfois 4 px et le calendrier bascule au-dessus pour rien.
    const slack = 16;
    const wanted = Math.max(
      inputRect.bottom + gap + calendarHeight + margin + slack - window.innerHeight,
      anchorRect.bottom + margin - window.innerHeight,
      0
    );
    // Bornes : garder la ligne sous la barre du haut, et ne pas défiler
    // au-delà du bas de la page.
    const headroom = anchorRect.top - topLimit;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
    const delta = Math.min(wanted, headroom, Math.max(scrollable, 0));

    if (delta > 1) window.scrollBy({ top: delta });
  }, []);

  // Mettre à jour la position lors de l'ouverture
  useEffect(() => {
    if (!isCalendarOpen) return;
    scrollForCalendar();
    updateCalendarPosition();
    // Passages suivants : tant que le calendrier n'est pas rendu, sa hauteur
    // n'est qu'estimée, et la ligne d'édition gagne encore quelques pixels en
    // se dépliant — une vingtaine d'écart suffit à le faire basculer au-dessus
    // alors que la place existait en dessous. Chaque passage est sans effet
    // quand la place est déjà faite.
    const frame = requestAnimationFrame(() => {
      scrollForCalendar();
      updateCalendarPosition();
    });
    const settle = setTimeout(() => {
      scrollForCalendar();
      updateCalendarPosition();
    }, 150);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settle);
    };
  }, [isCalendarOpen, scrollForCalendar, updateCalendarPosition]);

  // Mettre à jour la position lors du scroll ou resize
  useEffect(() => {
    if (!isCalendarOpen) return;

    const handleScrollOrResize = () => {
      updateCalendarPosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isCalendarOpen, updateCalendarPosition]);

  // Fermer le calendrier au clic extérieur
  useEffect(() => {
    if (!isCalendarOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        inputRef.current && !inputRef.current.contains(target) &&
        calendarRef.current && !calendarRef.current.contains(target)
      ) {
        closeCalendar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCalendarOpen]);

  // Compléter une date partielle avec le mois/année par défaut
  const completePartialDate = useCallback((partial: string): string | null => {
    // Extraire les chiffres seulement
    const digitsOnly = partial.replace(/[^\d]/g, '');

    if (digitsOnly.length === 0) return null;

    // Déterminer le mois et l'année par défaut
    const targetMonth = defaultMonth || new Date().getMonth() + 1;
    const targetYear = defaultYear || new Date().getFullYear();

    let day: number;
    let month: number;
    let year: number;

    if (digitsOnly.length <= 2) {
      // Seulement le jour: "1" ou "12"
      day = parseInt(digitsOnly, 10);
      month = targetMonth;
      year = targetYear;
    } else if (digitsOnly.length <= 4) {
      // Jour et mois partiel ou complet: "121" ou "1201"
      day = parseInt(digitsOnly.slice(0, 2), 10);
      month = parseInt(digitsOnly.slice(2), 10);
      // Si le mois n'a qu'un chiffre et est valide (1-9), l'utiliser
      // Sinon, essayer de l'interpréter
      if (digitsOnly.length === 3) {
        // "121" -> jour 12, mois 1
        month = parseInt(digitsOnly.slice(2, 3), 10);
      }
      year = targetYear;
    } else {
      // Date avec année partielle ou complète
      day = parseInt(digitsOnly.slice(0, 2), 10);
      month = parseInt(digitsOnly.slice(2, 4), 10);
      const yearPart = digitsOnly.slice(4);
      if (yearPart.length === 4) {
        year = parseInt(yearPart, 10);
      } else if (yearPart.length === 2) {
        // "26" -> "2026"
        year = 2000 + parseInt(yearPart, 10);
      } else {
        // Année incomplète, utiliser l'année par défaut
        year = targetYear;
      }
    }

    // Valider la date
    if (!isValidDate(day, month, year)) return null;

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }, [defaultMonth, defaultYear]);

  // Date hors de la plage à confirmer (la valeur courante est déjà acquise)
  const needsConfirmation = useCallback((isoDate: string) => (
    !!confirmOutside && isoDate !== value &&
    (isoDate < confirmOutside.min || isoDate > confirmOutside.max)
  ), [confirmOutside, value]);

  // Appliquer une date valide et dans les bornes, ou la soumettre à confirmation
  const commitDate = useCallback((isoDate: string): 'applied' | 'pending' => {
    clearError();
    if (confirmOutside && needsConfirmation(isoDate)) {
      setDisplayValue(formatToDisplay(value, compact));
      confirmOutside.onRequest(isoDate);
      return 'pending';
    }
    onChange(isoDate);
    setDisplayValue(formatToDisplay(isoDate, compact));
    return 'applied';
  }, [confirmOutside, needsConfirmation, value, compact, onChange, clearError]);

  // Valider et appliquer la date (utilisé par Tab, Entrée et le blur)
  const validateAndApply = useCallback((): 'applied' | 'pending' | 'rejected' => {
    // Si la zone est vide, ne rien faire (ne pas préremplir, ne pas effacer l'erreur existante)
    if (!displayValue || displayValue.trim() === '') {
      return 'rejected';
    }

    // Saisie inchangée : rien à valider. En compact, la recompléter avec
    // l'année par défaut déplacerait une date d'une autre année.
    if (value && displayValue === formatToDisplay(value, compact)) {
      clearError();
      return 'applied';
    }

    // Si la date est complète et valide, la transmettre
    // En mode compact, une date complète fait 5 chars (jj/mm), en normal 10 chars (jj/mm/aaaa)
    const fullLength = compact ? 5 : 10;
    const parsedFull = displayValue.length === fullLength && !compact ? parseDisplayDate(displayValue) : null;
    // Sinon, essayer de compléter une date partielle (ou date compact jj/mm)
    const isoDate = parsedFull ? formatToISO(displayValue) : completePartialDate(displayValue);

    if (!isoDate) {
      // Date invalide - revenir à la valeur précédente
      showError(t('dateErrors.invalid'));
      setDisplayValue(formatToDisplay(value, compact));
      return 'rejected';
    }

    // Vérifier les limites min/max
    if (minDate && isoDate < minDate) {
      showError(minDateError || t('dateErrors.beforeMin'));
      setDisplayValue(formatToDisplay(value, compact));
      return 'rejected';
    }
    if (maxDate && isoDate > maxDate) {
      showError(maxDateError || t('dateErrors.afterMax'));
      setDisplayValue(formatToDisplay(value, compact));
      return 'rejected';
    }

    return commitDate(isoDate);
  }, [displayValue, value, compact, minDate, maxDate, minDateError, maxDateError, completePartialDate, commitDate, showError, clearError]);

  // Gérer la saisie avec formatage automatique
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Effacer le message d'erreur dès que l'utilisateur commence à saisir
    clearError();

    // Signaler le début de la saisie (une seule fois)
    if (!hasStartedInputRef.current) {
      hasStartedInputRef.current = true;
      onInputStart?.();
    }

    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;

    // Compter les chiffres avant la position du curseur dans l'ancienne valeur
    const digitsBeforeCursor = newValue.slice(0, cursorPosition).replace(/[^\d]/g, '').length;

    // Supprimer tout sauf les chiffres
    const digitsOnly = newValue.replace(/[^\d]/g, '');

    // Limiter à 4 chiffres en compact (jj/mm), 8 en normal (jj/mm/aaaa)
    const maxDigits = compact ? 4 : 8;
    const truncated = digitsOnly.slice(0, maxDigits);

    // Formater avec les /
    let formatted = '';
    for (let i = 0; i < truncated.length; i++) {
      if (compact) {
        // En compact : seul séparateur après le jour (position 2)
        if (i === 2) formatted += '/';
      } else {
        if (i === 2 || i === 4) formatted += '/';
      }
      formatted += truncated[i];
    }

    setDisplayValue(formatted);

    // Ouvrir le calendrier si on commence à taper
    if (!isCalendarOpen && formatted.length > 0) {
      setIsCalendarOpen(true);
    }

    // Une date à confirmer n'est pas appliquée en cours de frappe : la demande
    // part à la validation (Entrée, Tab, blur).

    // En mode compact, valider quand on a jj/mm (5 chars)
    if (compact && formatted.length === 5) {
      const completedDate = completePartialDate(formatted);
      if (completedDate) {
        if (minDate && completedDate < minDate) return;
        if (maxDate && completedDate > maxDate) return;
        if (!needsConfirmation(completedDate)) onChange(completedDate);
        const [year, month] = completedDate.split('-').map(Number);
        setCalendarMonth({ month, year });
      }
    }

    // En mode normal, valider quand la date est complète (10 chars)
    if (!compact && formatted.length === 10) {
      const parsed = parseDisplayDate(formatted);
      if (parsed) {
        const isoDate = formatToISO(formatted);

        // Vérifier les limites min/max
        if (minDate && isoDate < minDate) return;
        if (maxDate && isoDate > maxDate) return;

        if (!needsConfirmation(isoDate)) onChange(isoDate);
        setCalendarMonth({ month: parsed.month, year: parsed.year });
      }
    }

    // Repositionner le curseur correctement après le formatage
    setTimeout(() => {
      if (inputRef.current) {
        // Trouver la position du curseur dans la chaîne formatée
        // en comptant les chiffres + les / ajoutés avant
        let newCursor = 0;
        let digitCount = 0;
        for (let i = 0; i < formatted.length && digitCount < digitsBeforeCursor; i++) {
          newCursor++;
          if (formatted[i] !== '/') {
            digitCount++;
          }
        }
        inputRef.current.setSelectionRange(newCursor, newCursor);
      }
    }, 0);
  };

  // Gérer la perte de focus
  const handleBlur = () => {
    validateAndApply();
    // Délai pour permettre le clic sur le calendrier avant de le fermer
    // Si l'utilisateur clique sur le calendrier, isClickingCalendarRef sera true
    setTimeout(() => {
      if (!isClickingCalendarRef.current) {
        closeCalendar();
        // Signaler la perte de focus (pour l'auto-save)
        onBlur?.();
      }
      isClickingCalendarRef.current = false;
    }, 150);
  };

  // Gérer les touches spéciales
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeCalendar();
      setDisplayValue(formatToDisplay(value, compact));
    } else if (e.key === 'Enter') {
      // Valider, fermer le calendrier et passer au champ suivant
      e.preventDefault();
      const result = validateAndApply();
      closeCalendar();
      // Date soumise à confirmation : la saisie n'est pas terminée
      if (result === 'pending') return;
      // Signaler la fin de saisie
      onConfirm?.();
      // Passer au champ suivant (uniquement en mode ajout, signalé par onConfirm)
      if (onConfirm) {
        focusNextField();
      }
    } else if (e.key === 'Tab') {
      // Valider avant de passer au champ suivant (Tab gère automatiquement le focus)
      const result = validateAndApply();
      closeCalendar();
      // Signaler la fin de saisie
      if (result !== 'pending') onConfirm?.();
    } else if (e.key === 'ArrowDown' && !isCalendarOpen) {
      setIsCalendarOpen(true);
      e.preventDefault();
    }
  };

  // Ouvrir le calendrier au focus
  const handleFocus = () => {
    if (!disabled) {
      setIsCalendarOpen(true);
      // S'assurer que le calendrier affiche le mois approprié
      setCalendarMonth(getDefaultCalendarMonth());

      // Sur mobile, le clavier virtuel rétrécit la fenêtre APRÈS le focus :
      // on refait le calcul de place une fois qu'il est monté.
      if (window.innerWidth < 768) {
        setTimeout(scrollForCalendar, 300);
      }
    }
  };

  // Sélectionner une date dans le calendrier
  const handleDateSelect = (day: number) => {
    const isoDate = `${calendarMonth.year}-${String(calendarMonth.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // Vérifier les limites
    if (minDate && isoDate < minDate) return;
    if (maxDate && isoDate > maxDate) return;

    const result = commitDate(isoDate);
    closeCalendar();
    if (result === 'pending') return;
    // Signaler la perte de focus (pour l'auto-save)
    onBlur?.();
    // Signaler la fin de saisie
    onConfirm?.();

    // Passer au champ suivant après sélection (uniquement en mode ajout, signalé par onConfirm)
    if (onConfirm) {
      focusNextField();
    }
  };

  // Navigation du calendrier
  const goToPreviousMonth = () => {
    setCalendarMonth(prev => {
      if (prev.month === 1) {
        return { month: 12, year: prev.year - 1 };
      }
      return { month: prev.month - 1, year: prev.year };
    });
  };

  const goToNextMonth = () => {
    setCalendarMonth(prev => {
      if (prev.month === 12) {
        return { month: 1, year: prev.year + 1 };
      }
      return { month: prev.month + 1, year: prev.year };
    });
  };

  // Générer les jours du calendrier
  const generateCalendarDays = () => {
    const firstDay = new Date(calendarMonth.year, calendarMonth.month - 1, 1);
    const lastDay = new Date(calendarMonth.year, calendarMonth.month, 0);
    const daysInMonth = lastDay.getDate();

    // Ajuster pour que la semaine commence le lundi (0 = lundi, 6 = dimanche)
    let startingDay = firstDay.getDay() - 1;
    if (startingDay < 0) startingDay = 6;

    const days: (number | null)[] = [];

    // Jours vides avant le premier jour
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Jours du mois
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  // Vérifier si un jour est désactivé
  const isDayDisabled = (day: number): boolean => {
    const isoDate = `${calendarMonth.year}-${String(calendarMonth.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (minDate && isoDate < minDate) return true;
    if (maxDate && isoDate > maxDate) return true;
    return false;
  };

  // Vérifier si un jour est sélectionné
  const isDaySelected = (day: number): boolean => {
    if (!value) return false;
    const [year, month, d] = value.split('-').map(Number);
    return day === d && calendarMonth.month === month && calendarMonth.year === year;
  };

  // Vérifier si un jour est aujourd'hui
  const isToday = (day: number): boolean => {
    const today = new Date();
    return day === today.getDate() &&
           calendarMonth.month === today.getMonth() + 1 &&
           calendarMonth.year === today.getFullYear();
  };

  // Rendu du calendrier
  const calendar = isCalendarOpen && !disabled && createPortal(
    <div
      ref={calendarRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg p-3"
      data-dateinput-calendar="true"
      style={{
        left: dropdownPosition.left,
        top: dropdownPosition.top,
        width: dropdownPosition.width,
      }}
      onMouseDown={(e) => {
        // Signaler qu'un clic sur le calendrier est en cours
        // Cela empêche handleBlur de fermer le calendrier
        isClickingCalendarRef.current = true;
        // Empêcher le shift de focus vers les boutons du calendrier :
        // l'input garde le focus, ce qui permet à un clic ultérieur en dehors
        // de la ligne de déclencher correctement le blur (et le commit).
        e.preventDefault();
      }}
    >
      {calendarView === 'days' && (
        <>
          {/* Header du calendrier - mois et année cliquables */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={goToPreviousMonth}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setCalendarView('months')}
              className="font-semibold text-gray-900 capitalize hover:bg-gray-100 rounded px-1.5 py-0.5 transition-colors"
            >
              {MONTHS[calendarMonth.month - 1]} {calendarMonth.year}
            </button>
            <button
              type="button"
              onClick={goToNextMonth}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Jours de la semaine */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Jours du mois */}
          <div className="grid grid-cols-7 gap-1">
            {generateCalendarDays().map((day, index) => (
              <div key={index} className="aspect-square">
                {day !== null && (
                  <button
                    type="button"
                    onClick={() => handleDateSelect(day)}
                    disabled={isDayDisabled(day)}
                    className={`
                      w-full h-full flex items-center justify-center text-sm rounded transition-colors
                      ${isDayDisabled(day)
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'hover:bg-primary-100 cursor-pointer'
                      }
                      ${isDaySelected(day)
                        ? 'bg-primary-600 text-white font-semibold hover:bg-primary-700'
                        : ''
                      }
                      ${isToday(day) && !isDaySelected(day)
                        ? 'ring-2 ring-primary-400 text-primary-600 font-bold'
                        : ''
                      }
                    `}
                  >
                    {day}
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {calendarView === 'months' && (
        <>
          {/* Header - navigation par année */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setCalendarMonth(prev => ({ ...prev, year: prev.year - 1 }))}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-gray-900">
              {calendarMonth.year}
            </span>
            <button
              type="button"
              onClick={() => setCalendarMonth(prev => ({ ...prev, year: prev.year + 1 }))}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Grille 4x3 de mois */}
          <div className="grid grid-cols-4 gap-1.5">
            {MONTH_KEYS.map((key, i) => {
              const month = i + 1;
              const isSelected = month === calendarMonth.month;
              const now = new Date();
              const isCurrent = month === now.getMonth() + 1 && calendarMonth.year === now.getFullYear();

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setCalendarMonth(prev => ({ ...prev, month }));
                    setCalendarView('days');
                  }}
                  className={clsx(
                    'px-2 py-2 text-xs font-medium rounded-lg transition-colors text-center',
                    isSelected
                      ? 'bg-primary-600 text-white'
                      : isCurrent
                        ? 'bg-primary-100 text-primary-700 font-semibold'
                        : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  {MONTHS_SHORT[i]}
                </button>
              );
            })}
          </div>
        </>
      )}

    </div>,
    document.body
  );

  return (
    <div className={`relative ${className}`}>
      <div className={`relative ${compact ? '' : 'w-fit'}`}>
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={compact ? 'jj/mm' : 'jj/mm/aaaa'}
          maxLength={compact ? 5 : 10}
          className={`input py-1.5 text-sm ${compact ? 'w-full px-1.5' : 'w-32 pr-8'} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${errorMessage ? 'border-red-500' : ''}`}
        />
        {!compact && (
          <button
            type="button"
            onClick={() => !disabled && setIsCalendarOpen(!isCalendarOpen)}
            disabled={disabled}
            className={`absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${disabled ? 'cursor-not-allowed' : ''}`}
            tabIndex={-1}
          >
            <Calendar className="w-4 h-4" />
          </button>
        )}
      </div>
      {calendar}
    </div>
  );
});

export default DateInput;
